'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { PERSONA_THEMES } from '@/shared/themes/personaTheme';
import type { Persona } from '@/shared/types/survey';

// ─────────────────────────────────────────────────
// CoachSpeech — the app's "voice" component
// Speech-bubble style card with icon + message.
// Entrance: spring slide-in from left with subtle scale.
// Text: staggered fade-in per line (like someone speaking).
// ─────────────────────────────────────────────────

const coachBubble = {
  initial: { opacity: 0, x: -8, scale: 0.97 },
  animate: { opacity: 1, x: 0, scale: 1 },
  transition: { type: 'spring' as const, stiffness: 350, damping: 30, mass: 0.8 },
};

// ── TextReveal: staggered line-by-line fade-in ──
function TextReveal({ text, className, style }: { text: string; className?: string; style?: React.CSSProperties }) {
  const prefersReduced = useReducedMotion();
  const lines = text.split('\n');

  if (prefersReduced || lines.length <= 1) {
    return (
      <p className={className} style={{ whiteSpace: 'pre-line', ...style }}>
        {text}
      </p>
    );
  }

  return (
    <p className={className} style={style}>
      {lines.map((line, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 + i * 0.08, duration: 0.3, ease: 'easeOut' }}
          className="block"
        >
          {line}
        </motion.span>
      ))}
    </p>
  );
}

export type CoachSpeechVariant = 'header' | 'card' | 'inline' | 'overlay';

interface CoachSpeechProps {
  message: string;
  subtitle?: string;
  icon?: string;
  persona: Persona;
  variant?: CoachSpeechVariant;
  className?: string;
}

export default function CoachSpeech({
  message,
  subtitle,
  icon,
  persona,
  variant = 'header',
  className = '',
}: CoachSpeechProps) {
  const theme = PERSONA_THEMES[persona];

  // 기본 여백 추가 (className에 mb가 없으면 mb-5 적용)
  const hasMarginBottom = /\bmb-/.test(className);
  const baseMargin = hasMarginBottom ? '' : 'mb-5';

  // ── inline: compact banner for mid-flow messages ──
  if (variant === 'inline') {
    return (
      <motion.div
        role="status"
        aria-live="polite"
        initial={coachBubble.initial}
        animate={coachBubble.animate}
        transition={coachBubble.transition}
        className={`flex items-center gap-2.5 rounded-xl px-4 py-3 ${baseMargin} ${className}`}
        style={{
          backgroundColor: `${theme.colors.primaryLight}`,
        }}
      >
        {icon && <span className="text-base flex-shrink-0" aria-hidden="true">{icon}</span>}
        <TextReveal
          text={message}
          className="text-sm font-medium leading-snug"
          style={{ color: theme.colors.primaryDark }}
        />
      </motion.div>
    );
  }

  // ── overlay: centered full-screen transition ──
  if (variant === 'overlay') {
    return (
      <motion.div
        role="status"
        aria-live="polite"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25, mass: 0.8 }}
        className={`text-center max-w-sm mx-auto rounded-2xl overflow-hidden ${baseMargin} ${className}`}
        style={{
          backgroundColor: theme.colors.primaryLight,
          border: `1px solid ${theme.colors.surfaceBorder}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        <div className="px-6 py-8 space-y-3">
          {icon && <div className="text-5xl mb-1" aria-hidden="true">{icon}</div>}
          <TextReveal
            text={message}
            className="text-lg font-bold leading-relaxed"
            style={{ color: theme.colors.primaryDark }}
          />
          {subtitle && (
            <p className="text-sm mt-1" style={{ color: theme.colors.primary, opacity: 0.8 }}>
              {subtitle}
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  // ── header / card: speech bubble with icon ──
  // Both get a visible background; 'card' gets an extra border + shadow
  const isCard = variant === 'card';

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={coachBubble.initial}
      animate={coachBubble.animate}
      transition={coachBubble.transition}
      className={`rounded-2xl overflow-hidden ${baseMargin} ${className}`}
      style={{
        backgroundColor: theme.colors.primaryLight,
        border: `1px solid ${theme.colors.surfaceBorder}`,
        boxShadow: isCard ? '0 1px 3px rgba(0,0,0,0.06)' : undefined,
      }}
    >
      <div className="flex items-start gap-3 p-4">
        {/* Icon circle */}
        {icon && (
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0"
            style={{
              backgroundColor: 'white',
              boxShadow: `0 0 0 2px ${theme.colors.surfaceBorder}`,
            }}
            aria-hidden="true"
          >
            {icon}
          </div>
        )}

        {/* Text */}
        <div className="flex-1 min-w-0 pt-0.5">
          <TextReveal
            text={message}
            className="text-[0.9375rem] font-medium leading-relaxed"
            style={{ color: '#1f2937' }}
          />
          {subtitle && (
            <p
              className="text-xs mt-1.5"
              style={{ color: theme.colors.primaryDark, opacity: 0.7 }}
            >
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
